import { Component, computed, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  ClientInfoResponse,
  ClientsClient,
  CompaniesClient,
  ISetPasswordCommand,
  IUpdateCompanyInfoCommand,
  IUpdateContactCommand,
  LoginUsersClient,
  SetPasswordCommand,
  UpdateCompanyInfoCommand,
  UpdateContactCommand,
} from '../../../infrastructure/shared/model/fss.api';
import { ControlsOf } from '../../../infrastructure/shared/forms/forms-common';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../infrastructure/shared/ui/field/field';
import { TextInput } from '../../../infrastructure/shared/ui/text-input/text-input';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Mail01 } from '../../../infrastructure/shared/icons/mail-01/mail-01';
import { Phone } from '../../../infrastructure/shared/icons/phone/phone';
import { Hash02 } from '../../../infrastructure/shared/icons/hash-02/hash-02';
import { HomeLine } from '../../../infrastructure/shared/icons/home-line/home-line';
import { MarkerPin01 } from '../../../infrastructure/shared/icons/marker-pin-01/marker-pin-01';
import { Map01 } from '../../../infrastructure/shared/icons/map-01/map-01';
import { Lock03 } from '../../../infrastructure/shared/icons/lock-03/lock-03';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { UserService } from '../../../infrastructure/shared/services/user.service';

type SettingsSection = 'basic' | 'contact' | 'password';

interface SectionItem {
  key: SettingsSection;
  label: string;
}

const SECTIONS: SectionItem[] = [
  { key: 'basic', label: 'Osnovni podaci' },
  { key: 'contact', label: 'Kontakt informacije' },
  { key: 'password', label: 'Lozinka' },
];

const required = {
  nonNullable: true as const,
  validators: [Validators.required],
};

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPwd = group.get('newPassword')?.value;
  const confirm = group.get('confirmPassword')?.value;
  if (!newPwd || !confirm) return null;
  return newPwd === confirm ? null : { passwordsMismatch: true };
}

import { PasswordRequirements } from '../../../infrastructure/shared/components/password-requirements/password-requirements';

@Component({
  standalone: true,
  selector: 'app-company-settings',
  imports: [
    PasswordRequirements,
    PageComponent,
    ButtonComponent,
    FieldComponent,
    TextInput,
    Building05,
    UserCircle,
    Mail01,
    Phone,
    Hash02,
    HomeLine,
    MarkerPin01,
    Map01,
    Lock03,
  ],
  templateUrl: './company-settings.html',
  styleUrl: './company-settings.scss',
})
export class CompanySettings {
  private readonly _fb = inject(FormBuilder);
  private readonly _clients = inject(ClientsClient);
  private readonly _companies = inject(CompaniesClient);
  private readonly _loginUsers = inject(LoginUsersClient);
  private readonly _userService = inject(UserService);
  private readonly _alert = inject(AlertService);
  private readonly _loader = inject(LoaderService);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly SECTIONS = SECTIONS;

  protected activeSection = signal<SettingsSection>('basic');
  protected client = signal<ClientInfoResponse | null>(null);

  protected headerName = computed(() => this.client()?.name ?? '');
  protected headerContact = computed(
    () => this.client()?.contactFullName ?? '',
  );
  protected headerEmail = computed(() => this.client()?.email ?? '');
  protected headerPhone = computed(() => this.client()?.phone ?? '');

  protected basicForm = this._fb.group<ControlsOf<IUpdateCompanyInfoCommand>>({
    name: new FormControl('', required),
    pib: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{9}$/),
      ],
    }),
    mb: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{8}$/),
      ],
    }),
    address: new FormControl('', required),
    city: new FormControl('', required),
    postalCode: new FormControl('', required),
  });

  protected contactForm = this._fb.group<ControlsOf<IUpdateContactCommand>>({
    firstName: new FormControl('', required),
    lastName: new FormControl('', required),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    phone: new FormControl('', required),
  });

  protected passwordForm = this._fb.group<ControlsOf<ISetPasswordCommand>>(
    {
      oldPassword: new FormControl('', required),
      newPassword: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6)],
      }),
      confirmPassword: new FormControl('', required),
    },
    { validators: passwordsMatchValidator },
  );

  constructor() {
    this._loadClient();
  }

  protected setSection(section: SettingsSection): void {
    this.activeSection.set(section);
  }

  protected isActive(section: SettingsSection): boolean {
    return this.activeSection() === section;
  }

  protected async saveBasic(): Promise<void> {
    this.basicForm.markAllAsTouched();
    if (this.basicForm.invalid) {
      if (this.basicForm.controls.pib.errors?.['pattern']) {
        this._alert.addFailedMsg('PIB mora imati tačno 9 cifara.');
      } else if (this.basicForm.controls.mb.errors?.['pattern']) {
        this._alert.addFailedMsg('Matični broj mora imati tačno 8 cifara.');
      }
      return;
    }

    const cmd = new UpdateCompanyInfoCommand(this.basicForm.getRawValue());

    this._loader.setLoadingText = 'Čuvanje izmena...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._companies.updateCompanyInfo(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Izmene su uspešno sačuvane.');
      await this._loadClient();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  protected async saveContact(): Promise<void> {
    this.contactForm.markAllAsTouched();
    if (this.contactForm.invalid) return;

    const cmd = new UpdateContactCommand(this.contactForm.getRawValue());

    this._loader.setLoadingText = 'Čuvanje izmena...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._companies.updateContact(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Izmene su uspešno sačuvane.');
      await this._loadClient();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  protected async savePassword(): Promise<void> {
    this.passwordForm.markAllAsTouched();

    if (this.passwordForm.errors?.['passwordsMismatch']) {
      this._alert.addFailedMsg('Lozinke se ne poklapaju.');
      return;
    }
    if (this.passwordForm.invalid) return;

    const cmd = new SetPasswordCommand(this.passwordForm.getRawValue());

    this._loader.setLoadingText = 'Promena lozinke...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._loginUsers.setPassword(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Izmene su uspešno sačuvane.');
      this.passwordForm.reset();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  private async _loadClient(): Promise<void> {
    const res = await firstValueFrom(
      this._clients.getClient(this._userService.getUserData.id),
    );
    const data = res.result;
    if (!data) return;

    this.client.set(data);

    const { firstName, lastName } = this._splitFullName(data.contactFullName);

    this.basicForm.setValue({
      name: data.name ?? '',
      pib: data.pib ?? '',
      mb: (data as any).mb ?? '',
      address: data.address ?? '',
      city: data.city ?? '',
      postalCode: data.postalCode ?? '',
    });

    this.contactForm.setValue({
      firstName,
      lastName,
      email: data.email ?? '',
      phone: data.phone ?? '',
    });
  }

  private _splitFullName(fullName: string | undefined): {
    firstName: string;
    lastName: string;
  } {
    if (!fullName) return { firstName: '', lastName: '' };
    const trimmed = fullName.trim();
    const idx = trimmed.indexOf(' ');
    if (idx === -1) return { firstName: trimmed, lastName: '' };
    return {
      firstName: trimmed.slice(0, idx),
      lastName: trimmed.slice(idx + 1).trim(),
    };
  }
}
